<script setup>
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import ReportSectionCard from '../ReportSectionCard.vue';
import {
  featurePresentation,
  formatModel,
  formatTokens,
  sourceLabel,
} from './aiUsagePresentation';

defineProps({
  events: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  page: { type: Number, default: 1 },
  totalPages: { type: Number, default: 0 },
  totalCount: { type: Number, default: 0 },
});

const emit = defineEmits(['update:page']);

const { t } = useI18n();

const EMPTY_VALUE = '-';

const formatDate = value => {
  if (!value) return EMPTY_VALUE;

  return new Date(value).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const conversationRoute = event => {
  const displayId =
    event.conversation?.display_id || event.conversation_display_id;

  if (!displayId) return null;

  return {
    name: 'inbox_conversation',
    params: { conversation_id: displayId },
  };
};

const conversationLabel = event =>
  `#${event.conversation?.display_id || event.conversation_display_id}`;

const metadataText = metadata => {
  if (!metadata || !Object.keys(metadata).length) return '';

  return JSON.stringify(metadata, null, 2);
};

const presentation = event => featurePresentation(event.feature_name, t);
</script>

<template>
  <ReportSectionCard
    :title="t('AI_USAGE_REPORT.LOGS.TITLE')"
    :description="t('AI_USAGE_REPORT.LOGS.DESCRIPTION')"
    icon="i-hugeicons-file-search"
    flush
  >
    <template #actions>
      <span
        class="rounded-md bg-n-solid-2 px-2 py-1 text-xs font-medium tabular-nums text-n-slate-11"
      >
        {{
          t(
            'AI_USAGE_REPORT.LOGS.RECORDS',
            { count: formatTokens(totalCount) },
            totalCount
          )
        }}
      </span>
    </template>

    <div
      v-if="!loading && !events.length"
      class="flex flex-col items-center gap-2 border-t border-n-weak px-5 py-12 text-center dark:border-n-strong"
    >
      <span
        class="grid size-11 place-items-center rounded-xl bg-n-solid-2 text-n-slate-10"
        aria-hidden="true"
      >
        <span class="i-hugeicons-file-search size-5" />
      </span>
      <p class="mb-0 text-sm font-semibold text-n-slate-12">
        {{ t('AI_USAGE_REPORT.LOGS.EMPTY_TITLE') }}
      </p>
      <p class="mb-0 text-sm text-n-slate-11">
        {{ t('AI_USAGE_REPORT.LOGS.EMPTY_DESCRIPTION') }}
      </p>
    </div>

    <div
      v-else
      class="overflow-x-auto border-t border-n-weak dark:border-n-strong"
    >
      <table class="w-full min-w-[980px] text-left text-sm">
        <thead
          class="sticky top-0 z-[1] bg-n-solid-2 text-xs font-medium text-n-slate-11"
        >
          <tr>
            <th class="px-5 py-2.5 font-medium">
              {{ t('AI_USAGE_REPORT.LOGS.COLUMNS.TIME') }}
            </th>
            <th class="px-4 py-2.5 font-medium">
              {{ t('AI_USAGE_REPORT.LOGS.COLUMNS.CONVERSATION') }}
            </th>
            <th class="px-4 py-2.5 font-medium">
              {{ t('AI_USAGE_REPORT.LOGS.COLUMNS.FEATURE') }}
            </th>
            <th class="px-4 py-2.5 font-medium">
              {{ t('AI_USAGE_REPORT.LOGS.COLUMNS.MODEL') }}
            </th>
            <th class="px-4 py-2.5 text-right font-medium">
              {{ t('AI_USAGE_REPORT.LOGS.TOKEN_INPUT') }}
            </th>
            <th class="px-4 py-2.5 text-right font-medium">
              {{ t('AI_USAGE_REPORT.LOGS.TOKEN_OUTPUT') }}
            </th>
            <th class="px-4 py-2.5 text-right font-medium">
              {{ t('AI_USAGE_REPORT.LOGS.TOKEN_TOTAL') }}
            </th>
            <th class="px-5 py-2.5 text-right font-medium">
              {{ t('AI_USAGE_REPORT.LOGS.COLUMNS.DETAILS') }}
            </th>
          </tr>
        </thead>
        <tbody v-if="loading" class="divide-y divide-n-weak" aria-busy="true">
          <tr v-for="index in 5" :key="index">
            <td v-for="cell in 8" :key="cell" class="px-4 py-3.5">
              <span
                class="block h-4 rounded bg-n-alpha-2 motion-safe:animate-pulse"
              />
            </td>
          </tr>
        </tbody>
        <tbody v-else class="divide-y divide-n-weak">
          <tr
            v-for="event in events"
            :key="event.id"
            data-test-id="aiUsageLogRow"
            class="align-top transition-colors hover:bg-n-alpha-1"
          >
            <td
              class="whitespace-nowrap px-5 py-3 tabular-nums text-n-slate-11"
            >
              {{ formatDate(event.occurred_at) }}
            </td>
            <td class="whitespace-nowrap px-4 py-3">
              <router-link
                v-if="conversationRoute(event)"
                :to="conversationRoute(event)"
                class="font-medium tabular-nums text-n-brand hover:underline"
              >
                {{ conversationLabel(event) }}
              </router-link>
              <span v-else class="text-n-slate-10">{{ EMPTY_VALUE }}</span>
            </td>
            <td class="max-w-[18rem] px-4 py-3">
              <div class="flex min-w-0 items-center gap-2.5">
                <span
                  class="grid size-8 shrink-0 place-items-center rounded-lg bg-n-solid-2 text-n-slate-11"
                  aria-hidden="true"
                >
                  <span :class="presentation(event).icon" class="size-4" />
                </span>
                <div class="min-w-0">
                  <p
                    class="mb-0 truncate font-medium text-n-slate-12"
                    :title="event.feature_name"
                  >
                    {{ presentation(event).label }}
                  </p>
                  <p
                    class="mb-0 truncate text-xs text-n-slate-11"
                    :title="event.source_type"
                  >
                    {{ sourceLabel(event.source_type, t) }}
                  </p>
                </div>
              </div>
            </td>
            <td class="px-4 py-3">
              <span
                class="inline-flex max-w-[12rem] truncate rounded-md bg-n-solid-2 px-2 py-0.5 text-xs font-medium text-n-slate-11"
                :title="event.model || event.provider"
              >
                {{ formatModel(event.model || event.provider) }}
              </span>
            </td>
            <td class="px-4 py-3 text-right tabular-nums text-n-slate-11">
              {{ formatTokens(event.input_tokens) }}
            </td>
            <td class="px-4 py-3 text-right tabular-nums text-n-slate-11">
              {{ formatTokens(event.output_tokens) }}
            </td>
            <td
              class="px-4 py-3 text-right font-semibold tabular-nums text-n-slate-12"
            >
              {{ formatTokens(event.total_tokens) }}
            </td>
            <td class="px-5 py-3 text-right">
              <details
                v-if="metadataText(event.metadata)"
                class="ml-auto max-w-xs"
              >
                <summary
                  class="inline-flex cursor-pointer list-none items-center gap-1 whitespace-nowrap rounded-lg px-2 py-1 text-sm font-medium text-n-brand hover:bg-n-alpha-2"
                >
                  <span class="i-hugeicons-file-search size-4" />
                  {{ t('AI_USAGE_REPORT.LOGS.SEE_LOG') }}
                </summary>
                <code
                  class="mt-2 block max-h-40 overflow-auto whitespace-pre rounded-lg bg-n-solid-2 p-3 text-left text-xs text-n-slate-11"
                  >{{ metadataText(event.metadata) }}</code
                >
              </details>
              <span v-else class="text-n-slate-10">{{ EMPTY_VALUE }}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div
      v-if="totalPages > 1"
      class="flex flex-wrap items-center justify-between gap-3 border-t border-n-weak px-5 py-3 dark:border-n-strong"
    >
      <Button
        :label="t('AI_USAGE_REPORT.LOGS.PREVIOUS')"
        icon="i-hugeicons-arrow-left-01"
        color="slate"
        variant="outline"
        size="sm"
        :disabled="page <= 1 || loading"
        @click="emit('update:page', page - 1)"
      />
      <span class="text-xs tabular-nums text-n-slate-11">
        {{ t('AI_USAGE_REPORT.LOGS.PAGE', { page, total: totalPages }) }}
      </span>
      <Button
        :label="t('AI_USAGE_REPORT.LOGS.NEXT')"
        icon="i-hugeicons-arrow-right-01"
        trailing-icon
        color="slate"
        variant="outline"
        size="sm"
        :disabled="page >= totalPages || loading"
        @click="emit('update:page', page + 1)"
      />
    </div>
  </ReportSectionCard>
</template>
